import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import {
  depositBankCoins,
  getBankPoints,
  getBankStatus,
  withdrawBankCoins,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function BankPage() {
  const [params] = useSearchParams()
  const highlightId = params.get("pointId")
  const gps = usePlayerGpsState()
  const [amount, setAmount] = useState("100")
  const [activePoint] = useState<string | null>(highlightId)
  const { syncGameState } = useGuardiansPassport()

  const pointsQuery = useQuery({
    queryKey: ["bank-points"],
    queryFn: getBankPoints,
  })

  const statusQuery = useQuery({
    queryKey: ["bank-status"],
    queryFn: getBankStatus,
  })

  const deposit = useMutation({
    mutationFn: (pointId: string) => {
      const n = Math.floor(Number(amount))
      if (!gps || !Number.isFinite(n) || n <= 0) {
        throw new Error("Укажите сумму и точку банка")
      }
      return depositBankCoins({
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
        amount: n,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      void statusQuery.refetch()
    },
  })

  const withdraw = useMutation({
    mutationFn: (pointId: string) => {
      const n = Math.floor(Number(amount))
      if (!gps || !Number.isFinite(n) || n <= 0) {
        throw new Error("Укажите сумму и точку банка")
      }
      return withdrawBankCoins({
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
        amount: n,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      void statusQuery.refetch()
    },
  })

  const status = statusQuery.data
  const points = (pointsQuery.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      latitude: p.latitude,
      longitude: p.longitude,
      radiusMeters: p.radiusMeters,
    })
  )

  return (
    <GameScreenLayout title="Банк" backTo="/play">
      {status ? (
        <dl className="mb-4 grid grid-cols-2 gap-2 text-xs">
          <div>
            <dt className="text-guardians-muted">В банке</dt>
            <dd className="text-guardians-accent">{status.bankCoins}</dd>
          </div>
          <div>
            <dt className="text-guardians-muted">Ёмкость</dt>
            <dd>{status.bankCapacity}</dd>
          </div>
          <div>
            <dt className="text-guardians-muted">Карт в банке</dt>
            <dd>{status.bankCardsCount}</dd>
          </div>
          <div>
            <dt className="text-guardians-muted">Слотов</dt>
            <dd>{status.bankSlotsLeft}</dd>
          </div>
        </dl>
      ) : null}

      <label className="mb-3 block text-xs text-guardians-muted">
        Сумма монет
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        />
      </label>

      <GeoPointsList
        points={points}
        gps={gps}
        highlightId={activePoint ?? highlightId}
        renderActions={({ point, inRange }) => (
          <div className="flex shrink-0 flex-col gap-1">
            <button
              type="button"
              disabled={!inRange}
              onClick={() => deposit.mutate(point.id)}
              className="rounded-lg bg-guardians-accent px-2 py-1 text-[10px] text-guardians-bg disabled:opacity-40"
            >
              Внести
            </button>
            <button
              type="button"
              disabled={!inRange}
              onClick={() => withdraw.mutate(point.id)}
              className="rounded-lg border border-white/20 px-2 py-1 text-[10px] disabled:opacity-40"
            >
              Снять
            </button>
          </div>
        )}
      />

      {(deposit.isError || withdraw.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(
            deposit.error ?? withdraw.error,
            "Операция не удалась"
          )}
        </p>
      )}
    </GameScreenLayout>
  )
}
